import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";

/**
 * Wraps children in a route that requires authentication.
 *
 * Phase 2b2 — props (mutually inclusive: a `requirePlatform` route
 * implicitly satisfies a v1 `requireRole="admin"` for backward compat):
 *   - `requireRole="admin"`     legacy v1 — requires `role >= admin`.
 *                               Equivalent to `requireRoleV2 in
 *                               {tenant_admin, mssp_admin, platform_admin}`.
 *   - `requireRoleV2={[...]}`   list of accepted v2 roles. Use the
 *                               canonical wire strings (e.g.
 *                               `"platform_admin"`).
 *   - `requirePlatform`         shortcut for platform_admin/platform_ops.
 *   - `requireMssp`             shortcut for mssp_admin/mssp_analyst
 *                               OR any of the above.
 *
 * Layered checks: a user that fails any single requirement gets a
 * 403 page (rendered inline so the route still mounts the layout).
 */
export default function ProtectedRoute({
  children,
  requireRole,
  requireRoleV2,
  requirePlatform,
  requireMssp,
}) {
  const { t } = useTranslation("auth");
  const { user, ready } = useAuth();
  const location = useLocation();

  if (!ready) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-xs"
        style={{ background: "var(--color-bg)", color: "#64748b" }}
      >
        {t("status.authenticating")}
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Legacy v1 admin gate. We honor it independently so existing call
  // sites like `<ProtectedRoute requireRole="admin">` keep working.
  if (requireRole === "admin" && user.role !== "admin") {
    return <Forbidden message={t("forbidden.admin_required")} />;
  }

  const r = user.role_v2 ?? null;
  const isPlatform = r === "platform_admin" || r === "platform_ops";
  const isMssp = r === "mssp_admin" || r === "mssp_analyst";

  if (requirePlatform && !isPlatform) {
    return <Forbidden message={t("forbidden.platform_required")} />;
  }

  if (requireMssp && !isPlatform && !isMssp) {
    return <Forbidden message={t("forbidden.mssp_required")} />;
  }

  if (Array.isArray(requireRoleV2) && r && !requireRoleV2.includes(r)) {
    return <Forbidden message={t("forbidden.role_not_allowed", { role: r })} />;
  }

  return children;
}

function Forbidden({ message }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center text-sm"
      style={{ background: "var(--color-bg)", color: "#ef4444" }}
    >
      403 — {message}
    </div>
  );
}
